import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Drawer, BadgePill, PassCard } from '@/components/campus';
import InviteFriendsModal from '@/components/referral/InviteFriendsModal';
import { AdBanner } from '@/components/ads/AdDisplay';
import {
  Calendar,
  MapPin,
  Clock,
  Users,
  Search,
  Ticket,
  Sparkles,
  ChevronRight,
  Loader2,
  PartyPopper,
  CheckCircle,
  X,
  UserPlus,
  Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get dynamic heading based on time of day
const getDynamicHeading = () => {
  const hour = new Date().getHours();
  if (hour >= 17 || hour < 4) {
    return "What's Happening Tonight";
  } else if (hour >= 4 && hour < 12) {
    return "Good Morning! Upcoming Events";
  } else {
    return "Discover Events";
  }
};

// Format date helpers
const formatEventDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Tonight';
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatEventTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
};

// ==================== EVENT CARD ====================
const DiscoverEventCard = ({ event, onClick, featured = false }) => {
  const spotsRemaining = event.spots_remaining ?? (event.capacity - (event.guest_count || 0));
  const isFull = spotsRemaining <= 0;
  
  return (
    <div
      onClick={onClick}
      className={cn(
        "group cursor-pointer rounded-2xl border border-white/[0.08] bg-gradient-to-b from-graphite to-obsidian overflow-hidden transition-all duration-300 hover:border-electric-ice-blue/30 hover:shadow-lg hover:shadow-electric-ice-blue/5",
        featured && "md:col-span-2 md:row-span-2"
      )}
      data-testid={`event-card-${event.id}`}
    >
      {/* Image placeholder */}
      <div className={cn(
        "relative bg-gradient-to-br from-electric-ice-blue/20 via-cool-mint/10 to-transparent",
        featured ? "h-48 md:h-64" : "h-32"
      )}>
        <div className="absolute inset-0 flex items-center justify-center">
          <PartyPopper className={cn(
            "text-electric-ice-blue/30",
            featured ? "w-20 h-20" : "w-12 h-12"
          )} />
        </div>
        
        {/* Date badge */}
        <div className="absolute top-3 left-3">
          <div className="px-3 py-1.5 bg-obsidian/80 backdrop-blur-sm rounded-lg border border-white/[0.08]">
            <p className="text-xs font-semibold text-cool-mint">{formatEventDate(event.start_time)}</p>
          </div>
        </div>
        
        {/* Spots badge */}
        <div className="absolute top-3 right-3">
          <div className={cn(
            "px-3 py-1.5 rounded-lg border backdrop-blur-sm",
            isFull 
              ? "bg-signal-red/20 border-signal-red/30" 
              : spotsRemaining <= 10 
                ? "bg-soft-amber/20 border-soft-amber/30"
                : "bg-obsidian/80 border-white/[0.08]"
          )}>
            <p className={cn(
              "text-xs font-semibold font-mono",
              isFull ? "text-signal-red" : spotsRemaining <= 10 ? "text-soft-amber" : "text-soft-silver"
            )}>
              {isFull ? 'FULL' : `${spotsRemaining} spots`}
            </p>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className={cn(
            "font-semibold text-cloud-white group-hover:text-electric-ice-blue transition-colors line-clamp-2",
            featured ? "text-xl" : "text-base"
          )}>
            {event.event_name}
          </h3>
          <p className="text-sm text-muted-text mt-1">{event.organization_name}</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 text-soft-silver text-sm">
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-electric-ice-blue" />
            <span className="font-mono">{formatEventTime(event.start_time)}</span>
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-cool-mint" />
              <span className="truncate max-w-[120px]">{event.location}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-text" />
            <span className="text-xs text-muted-text font-mono">{event.guest_count || 0} going</span>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-text group-hover:text-electric-ice-blue group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </div>
  );
};

// ==================== EVENT DETAIL DRAWER ====================
const EventDetailDrawer = ({ 
  event, 
  isOpen, 
  onClose, 
  userRsvpStatus,
  onRsvp,
  isLoading,
  onInviteFriends,
  session
}) => {
  if (!event) return null;
  
  const spotsRemaining = event.spots_remaining ?? (event.capacity - (event.guest_count || 0));
  const isFull = spotsRemaining <= 0;
  const hasPass = userRsvpStatus?.approval_status === 'approved';
  
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="" width="lg">
      <div className="space-y-6">
        {/* Header Image */}
        <div className="relative h-48 -mx-6 -mt-6 bg-gradient-to-br from-electric-ice-blue/20 via-cool-mint/10 to-transparent flex items-center justify-center">
          <PartyPopper className="w-20 h-20 text-electric-ice-blue/30" />
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 bg-obsidian/50 hover:bg-obsidian/80"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Event Info */}
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-cloud-white">{event.event_name}</h2>
            <p className="text-soft-silver mt-1">Hosted by {event.organization_name}</p>
          </div>
          
          {/* Date/Time/Location */}
          <div className="grid gap-3">
            <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-xl border border-white/[0.06]">
              <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-electric-ice-blue" />
              </div>
              <div>
                <p className="text-sm text-muted-text">Date & Time</p>
                <p className="text-cloud-white font-medium">
                  {formatEventDate(event.start_time)} at {formatEventTime(event.start_time)}
                </p>
              </div>
            </div>
            
            {event.location && (
              <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-xl border border-white/[0.06]">
                <div className="w-10 h-10 rounded-lg bg-cool-mint/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-cool-mint" />
                </div>
                <div>
                  <p className="text-sm text-muted-text">Location</p>
                  <p className="text-cloud-white font-medium">{event.location}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-xl border border-white/[0.06]">
              <div className="w-10 h-10 rounded-lg bg-soft-amber/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-soft-amber" />
              </div>
              <div>
                <p className="text-sm text-muted-text">Capacity</p>
                <p className="text-cloud-white font-medium font-mono">
                  {event.guest_count || 0} / {event.capacity} 
                  <span className={cn(
                    "ml-2 text-sm",
                    isFull ? "text-signal-red" : spotsRemaining <= 10 ? "text-soft-amber" : "text-cool-mint"
                  )}>
                    ({isFull ? 'Full' : `${spotsRemaining} spots left`})
                  </span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Description */}
          {event.description && (
            <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
              <p className="text-sm text-muted-text mb-2">About</p>
              <p className="text-soft-silver leading-relaxed">{event.description}</p>
            </div>
          )}
        </div>
        
        {/* Invite Friends Button - show if user has a pass or is logged in */}
        {session && (
          <Button
            onClick={onInviteFriends}
            variant="outline"
            className="w-full border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
            data-testid="invite-friends-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Friends to This Event
          </Button>
        )}
        
        {/* CTA */}
        <div className="pt-4 border-t border-white/[0.06]">
          {userRsvpStatus?.approval_status === 'approved' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
                <CheckCircle className="w-5 h-5 text-cool-mint" />
                <span className="text-cool-mint font-medium">You're on the list!</span>
              </div>
              <Button
                onClick={() => window.location.href = '/my-pass'}
                className="w-full bg-cool-mint text-obsidian hover:bg-cool-mint/90"
              >
                <Ticket className="w-4 h-4 mr-2" />
                View My Pass
              </Button>
            </div>
          ) : userRsvpStatus?.approval_status === 'pending' ? (
            <div className="flex items-center gap-2 p-3 bg-soft-amber/10 rounded-lg border border-soft-amber/20">
              <Clock className="w-5 h-5 text-soft-amber" />
              <span className="text-soft-amber font-medium">Pass Requested — Pending Approval</span>
            </div>
          ) : userRsvpStatus?.approval_status === 'denied' ? (
            <div className="flex items-center gap-2 p-3 bg-signal-red/10 rounded-lg border border-signal-red/20">
              <X className="w-5 h-5 text-signal-red" />
              <span className="text-signal-red font-medium">Request Denied</span>
            </div>
          ) : (
            <Button
              onClick={onRsvp}
              disabled={isLoading || isFull}
              className="w-full bg-cool-mint text-obsidian hover:bg-cool-mint/90 h-12 text-base font-semibold"
              data-testid="request-pass-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Requesting...
                </>
              ) : isFull ? (
                'Event is Full'
              ) : (
                <>
                  <Ticket className="w-5 h-5 mr-2" />
                  Request Pass
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Drawer>
  );
};

// ==================== MAIN DISCOVER PAGE ====================
export const Discover = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [eventType, setEventType] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [userRsvpStatus, setUserRsvpStatus] = useState(null);
  const [isRsvping, setIsRsvping] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page_size: '50' });
      if (dateFilter) params.append('date_filter', dateFilter);
      if (eventType) params.append('event_type', eventType);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await fetch(`${API_URL}/api/discover/events?${params}`);
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, eventType, searchQuery]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  // Handle event click
  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    setIsDetailOpen(true);
    setUserRsvpStatus(null);
    
    // Fetch user's RSVP status if logged in
    if (session?.access_token) {
      try {
        const res = await fetch(`${API_URL}/api/discover/events/${event.id}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setUserRsvpStatus(data.user_rsvp_status);
          // Update event with latest stats
          setSelectedEvent(data);
        }
      } catch (error) {
        console.error('Failed to fetch event details:', error);
      }
    }
  };
  
  // Handle RSVP
  const handleRsvp = async () => {
    if (!session?.access_token) {
      toast.error('Please sign in to request a pass');
      navigate('/login');
      return;
    }
    
    setIsRsvping(true);
    try {
      const res = await fetch(`${API_URL}/api/discover/events/${selectedEvent.id}/rsvp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (res.ok) {
        toast.success('Pass requested! You\'ll be notified when approved.');
        setUserRsvpStatus({ approval_status: 'pending' });
        fetchEvents(); // Refresh counts
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to request pass');
      }
    } catch (error) {
      toast.error('Failed to request pass');
    } finally {
      setIsRsvping(false);
    }
  };
  
  // Search handler with debounce
  const handleSearch = (value) => {
    setSearchQuery(value);
  };
  
  // Featured event (first/largest event)
  const featuredEvent = events[0];
  const otherEvents = events.slice(1);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading events...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-8 pb-12" data-testid="discover-page">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-electric-ice-blue/10 rounded-full border border-electric-ice-blue/20">
          <Sparkles className="w-4 h-4 text-electric-ice-blue" />
          <span className="text-sm text-electric-ice-blue font-medium">Campus Events</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-cloud-white">
          {getDynamicHeading()}
        </h1>
        <p className="text-soft-silver max-w-md mx-auto">
          Find the hottest events on campus. Request passes and get ready to party.
        </p>
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search events..."
            className="pl-10 bg-graphite border-white/[0.08] text-cloud-white"
            data-testid="search-events-input"
          />
        </div>
        
        <Select value={dateFilter || undefined} onValueChange={(v) => setDateFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-40 bg-graphite border-white/[0.08] text-cloud-white">
            <SelectValue placeholder="When" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            <SelectItem value="all" className="text-cloud-white">All Dates</SelectItem>
            <SelectItem value="tonight" className="text-cloud-white">Tonight</SelectItem>
            <SelectItem value="this_week" className="text-cloud-white">This Week</SelectItem>
            <SelectItem value="this_weekend" className="text-cloud-white">This Weekend</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={eventType || undefined} onValueChange={(v) => setEventType(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-40 bg-graphite border-white/[0.08] text-cloud-white">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            <SelectItem value="all" className="text-cloud-white">All Types</SelectItem>
            <SelectItem value="party" className="text-cloud-white">Party</SelectItem>
            <SelectItem value="mixer" className="text-cloud-white">Mixer</SelectItem>
            <SelectItem value="formal" className="text-cloud-white">Formal</SelectItem>
            <SelectItem value="philanthropy" className="text-cloud-white">Philanthropy</SelectItem>
            <SelectItem value="social" className="text-cloud-white">Social</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Sponsored Ad Banner */}
      <AdBanner placement="discover_top" className="my-4" />
      
      {/* Events Grid */}
      {events.length === 0 ? (
        <div className="text-center py-16">
          <PartyPopper className="w-16 h-16 text-muted-text mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-cloud-white mb-2">No events found</h2>
          <p className="text-soft-silver">
            {searchQuery || dateFilter || eventType 
              ? 'Try adjusting your filters' 
              : 'Check back later for upcoming events'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Featured Event */}
          {featuredEvent && (
            <DiscoverEventCard
              event={featuredEvent}
              onClick={() => handleEventClick(featuredEvent)}
              featured={true}
            />
          )}
          
          {/* Other Events */}
          {otherEvents.map(event => (
            <DiscoverEventCard
              key={event.id}
              event={event}
              onClick={() => handleEventClick(event)}
            />
          ))}
        </div>
      )}
      
      {/* Event Detail Drawer */}
      <EventDetailDrawer
        event={selectedEvent}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        userRsvpStatus={userRsvpStatus}
        onRsvp={handleRsvp}
        isLoading={isRsvping}
        session={session}
        onInviteFriends={() => {
          setIsDetailOpen(false);
          setShowInviteModal(true);
        }}
      />
      
      {/* Invite Friends Modal */}
      {selectedEvent && (
        <InviteFriendsModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          event={selectedEvent}
          organizationId={selectedEvent.organization_id}
          organizationName={selectedEvent.organization_name}
          session={session}
          onSuccess={() => {
            toast.success('Friends invited successfully!');
          }}
        />
      )}
    </div>
  );
};

export default Discover;
